/**
 * Procedural 15x15 sub-tile shape masks.
 *
 * The original game stored these as sprite alpha masks (shapes.png). We don't
 * have the asset, so we generate geometrically equivalent masks: a tile's
 * foreground element occupies the pixels where the mask is set, the background
 * fills the rest. Triangles produce 45-degree diagonal walls; circles/rounded
 * shapes produce curved bumpers — both reflect correctly via the simulator's
 * 8-point collision sampling.
 */
import { Shape } from '../types.js';
import { TILE_SIZE } from '../constants.js';

const N = TILE_SIZE; // 15
const C = (N - 1) / 2; // 7

function dist2(x: number, y: number, cx: number, cy: number): number {
  const dx = x - cx;
  const dy = y - cy;
  return dx * dx + dy * dy;
}

function predicate(shape: Shape, x: number, y: number): boolean {
  const max = N - 1; // 14
  switch (shape) {
    case Shape.Blank:
      return false;
    case Shape.BigCircle:
      return dist2(x, y, C, C) <= 7.5 * 7.5;
    case Shape.SmallCircle:
      return dist2(x, y, C, C) <= 4.5 * 4.5;
    case Shape.Diamond:
      return Math.abs(x - C) + Math.abs(y - C) <= 7;
    // Diagonal half-tiles (the right angle sits in the named corner)
    case Shape.TriangleNW:
      return x + y <= max;
    case Shape.TriangleNE:
      return x >= y;
    case Shape.TriangleSE:
      return x + y >= max;
    case Shape.TriangleSW:
      return x <= y;
    // Rounded corners (convex quarter-disk bulging from the named corner)
    case Shape.RoundedNW:
      return dist2(x, y, 0, 0) <= max * max;
    case Shape.RoundedNE:
      return dist2(x, y, max, 0) <= max * max;
    case Shape.RoundedSE:
      return dist2(x, y, max, max) <= max * max;
    case Shape.RoundedSW:
      return dist2(x, y, 0, max) <= max * max;
    // Rounded sides (semicircle bulging from the named edge)
    case Shape.RoundedN:
      return dist2(x, y, C, 0) <= 7.5 * 7.5;
    case Shape.RoundedE:
      return dist2(x, y, max, C) <= 7.5 * 7.5;
    case Shape.RoundedS:
      return dist2(x, y, C, max) <= 7.5 * 7.5;
    case Shape.RoundedW:
      return dist2(x, y, 0, C) <= 7.5 * 7.5;
    // Triangles pointing toward the named edge
    case Shape.TriangleN:
      return x >= C - y / 2 && x <= C + y / 2;
    case Shape.TriangleS:
      return x >= C - (max - y) / 2 && x <= C + (max - y) / 2;
    case Shape.TriangleE:
      return y >= C - (max - x) / 2 && y <= C + (max - x) / 2;
    case Shape.TriangleW:
      return y >= C - x / 2 && y <= C + x / 2;
    case Shape.TriangleNS:
      return Math.abs(x - C) <= 3;
    case Shape.TriangleWE:
      return Math.abs(y - C) <= 3;
    case Shape.HalfW:
      return x <= C;
    case Shape.HalfS:
      return y >= C;
    case Shape.QuarterNE:
      return x >= C && y <= C;
    case Shape.QuarterSE:
      return x >= C && y >= C;
    case Shape.QuarterSW:
      return x <= C && y >= C;
    case Shape.QuarterNW:
      return x <= C && y <= C;
    default:
      return false;
  }
}

const cache = new Map<Shape, Uint8Array>();

/** Returns a 15x15 row-major boolean mask (1 = foreground) for a shape. */
export function shapeMask(shape: Shape): Uint8Array {
  let m = cache.get(shape);
  if (m) return m;
  m = new Uint8Array(N * N);
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      m[y * N + x] = predicate(shape, x, y) ? 1 : 0;
    }
  }
  cache.set(shape, m);
  return m;
}

export function shapeAt(shape: Shape, px: number, py: number): boolean {
  if (px < 0 || py < 0 || px >= N || py >= N) return false;
  return shapeMask(shape)[py * N + px] === 1;
}
